import * as React from 'react';
import styled, { css } from 'styled-components';
import { SvgIconProps } from '@material-ui/core/SvgIcon';

import { useSidebar } from '../organisms/AppShell/SidebarShell';

const SidebarMenuItem = ({
  onClick,
  isActive,
  icon: IconComp,
  title,
}: Props) => {
  const { collapsed } = useSidebar();

  return (
    <Row
      type="button"
      onClick={onClick}
      $active={!!isActive}
      $collapsed={collapsed}
      title={collapsed ? title : undefined}
      aria-current={isActive ? 'page' : undefined}
    >
      <IconSlot $active={!!isActive}>
        <IconComp />
      </IconSlot>
      {!collapsed && <Label $active={!!isActive}>{title}</Label>}
    </Row>
  );
};

const Row = styled.button<{ $active: boolean; $collapsed: boolean }>`
  position: relative;
  display: flex;
  align-items: center;
  gap: var(--space-3);
  width: 100%;
  height: 40px;
  padding: 0
    ${({ $collapsed }) => ($collapsed ? '0' : 'var(--space-3)')};
  justify-content: ${({ $collapsed }) =>
    $collapsed ? 'center' : 'flex-start'};
  border: 0;
  background: transparent;
  border-radius: var(--radius-md);
  color: rgba(255, 255, 255, 0.72);
  font-family: inherit;
  font-size: var(--fs-base);
  font-weight: var(--fw-medium);
  text-align: left;
  cursor: pointer;
  transition: background-color var(--motion-fast) var(--ease-standard),
    color var(--motion-fast) var(--ease-standard);

  &::before {
    content: '';
    position: absolute;
    left: -8px;
    top: 8px;
    bottom: 8px;
    width: 3px;
    border-radius: 0 var(--radius-sm) var(--radius-sm) 0;
    background: transparent;
    transition: background-color var(--motion-fast)
      var(--ease-standard);
  }

  &:hover {
    background: rgba(255, 255, 255, 0.08);
    color: var(--neutral-0);
  }

  &:focus-visible {
    outline: none;
    box-shadow: 0 0 0 2px rgba(255, 255, 255, 0.6);
  }

  ${({ $active }) =>
    $active &&
    css`
      background: rgba(255, 255, 255, 0.12);
      color: var(--neutral-0);

      &::before {
        background: var(--primary-200);
      }
    `}
`;

const IconSlot = styled.span<{ $active: boolean }>`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 24px;
  height: 24px;
  flex-shrink: 0;
  color: inherit;

  svg {
    width: 22px;
    height: 22px;
    fill: currentColor;
    path {
      fill: currentColor;
      color: inherit;
    }
  }

  .MuiIcon-root {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    width: 24px;
    height: 24px;
    overflow: visible;
    margin: 0;

    svg {
      width: 22px;
      height: 22px;
    }
  }
`;

const Label = styled.span<{ $active: boolean }>`
  flex: 1;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  font-weight: ${({ $active }) =>
    $active ? 'var(--fw-semibold)' : 'var(--fw-medium)'};
`;

interface Props {
  icon: React.ComponentType<SvgIconProps>;
  onClick?: (e: React.MouseEvent) => void;
  isActive: boolean;
  title: string;
}

export default SidebarMenuItem;
